import { adminBffFetch } from '@avila-roffe/api-clients-admin-bff';
import { toast } from '@avila-roffe/components-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface AppFeatureFlag {
  key: string;
  enabled: boolean;
  description: string;
}

export interface AppSettings {
  localization: {
    defaultLocale: string;
    availableLocales: string[];
    defaultTimezone: string;
    defaultCurrency: string;
    availableCurrencies: string[];
    dateFormat: string;
  };
  features: AppFeatureFlag[];
  ui: {
    defaultTheme: 'light' | 'dark' | 'system';
    itemsPerPage: number;
    defaultRoute: string;
    colorScheme?: 'light' | 'dark';
    themeName?: string;
    sidebarColor?: string;
    topbarColor?: string;
  };
  session: {
    timeoutMinutes: number;
    idleTimeoutMinutes: number;
  };
  integrations: {
    analytics: boolean;
    errorTracking: boolean;
    supportWidget: boolean;
  };
}

export interface ApplicationDto {
  id: string;
  slug: string;
  name: string;
  displayName?: string;
  description?: string;
  logoUrl?: string | null;
  settings?: AppSettings;
  createdAt: string;
  updatedAt: string;
}

type Envelope<T> = { data: { data: T } };

export function useFactoryApp() {
  return useQuery({
    queryKey: ['factory-app', 'code-factory'],
    queryFn: async () => {
      const res = await adminBffFetch<Envelope<ApplicationDto>>(
        '/api/v1/applications/code-factory',
      );
      return (res as Envelope<ApplicationDto>).data.data;
    },
  });
}

export function useFactoryAppSettings() {
  return useQuery({
    queryKey: ['factory-app-settings', 'code-factory'],
    queryFn: async () => {
      const res = await adminBffFetch<Envelope<AppSettings>>(
        '/api/v1/applications/code-factory/settings',
      );
      return (res as Envelope<AppSettings>).data.data;
    },
  });
}

export function useUpdateFactoryAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (settings: AppSettings) => {
      const res = await adminBffFetch<Envelope<AppSettings>>(
        '/api/v1/applications/code-factory/settings',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(settings),
        },
      );
      return (res as Envelope<AppSettings>).data.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['factory-app-settings', 'code-factory'], data);
      toast.success('Settings saved');
    },
  });
}
